import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { llmConfigSchema, llmConfigCreateSchema, type LlmConfigFormData } from '@/schemas/llm-config.schema';
import type { LlmConfig } from '@/types';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

interface LlmConfigFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialData?: LlmConfig;
  onSubmit: (data: LlmConfigFormData) => void;
  isSubmitting?: boolean;
}

const providerOptions = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'azure-openai', label: 'Azure OpenAI' },
] as const;

export function LlmConfigForm({
  open,
  onOpenChange,
  mode,
  initialData,
  onSubmit,
  isSubmitting = false,
}: LlmConfigFormProps) {
  const { toast } = useToast();

  const form = useForm<LlmConfigFormData>({
    resolver: zodResolver(mode === 'create' ? llmConfigCreateSchema : llmConfigSchema),
    defaultValues: {
      serviceId: '',
      providerType: 'openai',
      apiKey: '',
      modelName: '',
      baseEndpointUrl: '',
      defaultSystemPrompt: '',
      maxTokenLimit: 4096,
      temperature: 0.7,
    },
  });

  useEffect(() => {
    if (open && mode === 'edit' && initialData) {
      form.reset({
        serviceId: initialData.serviceId,
        providerType: initialData.providerType,
        apiKey: '', // always empty on edit - user must re-enter
        modelName: initialData.modelName,
        baseEndpointUrl: initialData.baseEndpointUrl ?? '',
        defaultSystemPrompt: initialData.defaultSystemPrompt ?? '',
        maxTokenLimit: initialData.maxTokenLimit,
        temperature: initialData.temperature,
      });
    } else if (open && mode === 'create') {
      form.reset({
        serviceId: '',
        providerType: 'openai',
        apiKey: '',
        modelName: '',
        baseEndpointUrl: '',
        defaultSystemPrompt: '',
        maxTokenLimit: 4096,
        temperature: 0.7,
      });
    }
  }, [open, mode, initialData, form]);

  function handleFormSubmit(data: LlmConfigFormData) {
    onSubmit(data);
    toast({
      title: mode === 'create' ? 'Configuration created' : 'Configuration updated',
      description: `LLM config for "${data.serviceId}" was saved successfully.`,
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[800px] w-[95vw]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create LLM Configuration' : 'Edit LLM Configuration'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="serviceId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service ID</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. my-chatbot-service"
                      disabled={mode === 'edit'}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="providerType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {providerOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>API Key</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={mode === 'edit' ? `Leave empty to keep current (${initialData?.maskedApiKey ?? '***'})` : 'Enter API key'}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="modelName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Model Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. gpt-4o" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="baseEndpointUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base Endpoint URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://api.example.com (optional)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="defaultSystemPrompt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default System Prompt</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter a default system prompt (optional)"
                      rows={12}
                      className="min-h-[200px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="maxTokenLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Token Limit</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="4096"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="temperature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Temperature</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        placeholder="0.7"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
